'use client';

/**
 * NotificationBadge Component
 *
 * Displays the unread notification count as a badge.
 * Animates on count change.
 */

import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface NotificationBadgeProps {
  count: number;
  max?: number;
  className?: string;
  animate?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'dot' | 'count';
}

export function NotificationBadge({
  count,
  max = 99,
  className,
  animate = true,
  size = 'md',
  variant = 'count',
}: NotificationBadgeProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [prevCount, setPrevCount] = useState(count);

  // Animate on count increase
  useEffect(() => {
    if (animate && count > prevCount && count > 0) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    }
    setPrevCount(count);
  }, [count, prevCount, animate]);

  // Don't render if count is 0
  if (count === 0) {
    return null;
  }

  // Size classes
  const sizeClasses = {
    sm: variant === 'dot' ? 'w-2 h-2' : 'min-w-[16px] h-4 text-[10px] px-1',
    md: variant === 'dot' ? 'w-2.5 h-2.5' : 'min-w-[20px] h-5 text-xs px-1.5',
    lg: variant === 'dot' ? 'w-3 h-3' : 'min-w-[24px] h-6 text-sm px-2',
  };

  // Dot variant (just a colored dot)
  if (variant === 'dot') {
    return (
      <span
        className={cn(
          'rounded-full bg-red-500 ring-2 ring-black',
          sizeClasses[size],
          isAnimating && 'animate-ping',
          className
        )}
      />
    );
  }

  // Count variant (number badge)
  const displayCount = count > max ? `${max}+` : count.toString();

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center font-medium rounded-full',
        'bg-red-500 text-white',
        'ring-2 ring-black',
        sizeClasses[size],
        isAnimating && 'animate-bounce',
        className
      )}
    >
      {displayCount}
    </span>
  );
}

/**
 * NotificationDot Component
 *
 * Simple animated dot indicator for new notifications.
 */
export function NotificationDot({
  show = true,
  pulse = true,
  className,
}: {
  show?: boolean;
  pulse?: boolean;
  className?: string;
}) {
  if (!show) {
    return null;
  }

  return (
    <span
      className={cn(
        'absolute top-1 right-1 w-3 h-3 bg-red-500 rounded-full',
        'ring-2 ring-black',
        pulse && 'animate-pulse',
        className
      )}
    />
  );
}

export default NotificationBadge;
